// MercenaryBar.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useSocket } from '../../contexts/SocketContext';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useAuth } from '../../contexts/AuthContext';
import styles from './MercenaryBar.module.css';
import battleNotificationSound from '../../sounds/battle_notification.mp3';
import Toast from '../Shared/Toast';
import { useAgeStageLabel } from '../../hooks/useAgeStageLabel';

interface MercenaryStats {
  id?: number;
  name?: string;
  race?: string;
  sex?: string;
  avatar?: number;
  age?: number;
  level?: number;
  health?: number;
  effectiveHealth?: number;
  maxHealth?: number;
  effectiveMaxHealth?: number;
  experience?: number;
  nextExperience?: number;
  previousExperience?: number;
  restedExp?: number;
  pooledXp?: number;
  rounds?: number;
  maxRounds?: number;
  form?: number;
  title?: string;
  silver?: number;
  reputation?: number;
  allowContracts?: boolean;
  xpLocked?: boolean;
  primarySkillPoints?: number;
  secondarySkillPoints?: number;
  isHospitalized?: boolean;
  hospitalizedUntil?: string;
  deathDate?: string | null;
}

interface ActivePotion {
  name: string;
  effect: string;
  effectEndTime: string;
  totalDurationMs: number;
}

interface PotionWithTimeLeft extends ActivePotion {
  remainingMs: number;
}

const MercenaryBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useSocket();
  const { mercenary } = useMercenary();
  const { setUser } = useAuth();
  const [mercenaryStats, setMercenaryStats] = useState<MercenaryStats | null>(mercenary);
  const [allowContracts, setAllowContracts] = useState<boolean>(false);
  const [xpLocked, setXpLocked] = useState<boolean>(false);
  const [spoilerMode, setSpoilerMode] = useState<1 | 2 | 3>(() => {
    const v = parseInt(localStorage.getItem('spoilerMode') || '1', 10);
    return (v === 1 || v === 2 || v === 3) ? v : 1;
  });

  const [battleNotification, setBattleNotification] = useState<{ battleId: number } | null>(null);
  const notificationAudioRef = useRef(new Audio(battleNotificationSound));
  const [levelUpNotification, setLevelUpNotification] = useState(false);
  const [regenTimerProgress, setRegenTimerProgress] = useState<number>(0);
  const [activePotions, setActivePotions] = useState<PotionWithTimeLeft[]>([]);
  const [activeEffectTooltip, setActiveEffectTooltip] = useState<number | null>(null);
  const [showHpTooltip, setShowHpTooltip] = useState(false);
  const ageBadgeRef = useRef<HTMLParagraphElement | null>(null);
  const [showAgeTooltip, setShowAgeTooltip] = useState(false);
  const [ageTooltipPosition, setAgeTooltipPosition] = useState<{ top: number; right: number } | null>(null);

  const [showAccountLinks, setShowAccountLinks] = useState(() => {
    const saved = localStorage.getItem('accountLinksOpen');
    return saved !== null ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('accountLinksOpen', JSON.stringify(showAccountLinks));
  }, [showAccountLinks]);

  const [pendingFriendRequests, setPendingFriendRequests] = useState<number>(0);
  const [unreadMails, setUnreadMails] = useState<number>(0);
  const [friendLoginNotification, setFriendLoginNotification] = useState<string | null>(null);
  
  // Hospitalization states
  const [hospitalizationInfo, setHospitalizationInfo] = useState<{
    battleReportId: number | null;
    hospitalizedUntil: string | null;
    timeRemaining: string;
  } | null>(null);

  useEffect(() => {
    if (socket) {
      const handleStatsUpdate = (data: { mercenaryId: number; updatedStats: Partial<MercenaryStats> }) => {
          const { mercenaryId, updatedStats } = data;
          const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '', 10);
          if (mercenaryId === activeMercenaryId) {
            // Only update local bar state, MercenaryContext already handles
            // the global setMercenary via its own socket handler.
            setMercenaryStats((prevState: MercenaryStats | null) => ({
                ...prevState,
                ...updatedStats,
            }));
          }
      };

      const handleBattleNotification = (data: number | { battleId: number; mercenaryId: number }) => {
        let battleId: number;
        let battlingMercenaryId: number | undefined;

        if (typeof data === 'number') {
          battleId = data;
        } else {
          battleId = data.battleId;
          battlingMercenaryId = data.mercenaryId;
        }

        if (battlingMercenaryId) {
           const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '0', 10);
           if (battlingMercenaryId !== activeMercenaryId) {
             return;
           }
        }

        if (location.pathname === '/duel') {
          navigate(`/battle-result/${battleId}`);
        } else {
          setBattleNotification({ battleId });
          notificationAudioRef.current.play().catch((error) => {
            console.error('Failed to play sound:', error);
          });

          setTimeout(() => {
            setBattleNotification(null);
          }, 30000);
        }
      };

      const handleLevelUp = (mercenaryId?: number) => {
        const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '0', 10);
        if (!mercenaryId || mercenaryId !== activeMercenaryId) return;
        setLevelUpNotification(true);
      };

      const handleRegen = (data: { mercenaryId: number; regen: { hp: number; rounds: number; maxHp?: number; maxRounds?: number; restedExp?: number } }) => {
        const { mercenaryId, regen } = data;
        const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '', 10);
        if (mercenaryId === activeMercenaryId) {
            const updates: any = {
                health: regen.hp,
                rounds: regen.rounds,
                restedExp: regen.restedExp,
            };
            if (regen.maxHp !== undefined) updates.maxHealth = regen.maxHp;
            if (regen.maxRounds !== undefined) updates.maxRounds = regen.maxRounds;
            // Only update local bar state, MercenaryContext already handles
            // the global setMercenary via its own socket handler.
            setMercenaryStats((prevState) => {
                if (!prevState) return prevState;
                return {
                    ...prevState,
                    ...updates,
                };
            });
        }
      };

      const handleFriendRequestHandled = () => {
        setPendingFriendRequests((prev) => Math.max(prev - 1, 0));
      };

      const handleNewMail = () => {
        setUnreadMails(prev => prev + 1);
      };

      const handleMailRead = () => {
        setUnreadMails(prev => Math.max(prev - 1, 0));
      };

      const handleFriendOnline = (friendData: { userId: number; username: string; displayName: string }) => {
        setFriendLoginNotification(`${friendData.displayName} has logged in!`);
      };

      const handleMercenaryDied = ({ battleReportId, mercenaryId: deadMercId }: { battleReportId: string | number; mercenaryId?: number }) => {
        // Only redirect if the dead mercenary is the one currently being played.
        // If the user killed their own alt, the event fires on their socket room
        // but should not affect the active mercenary's session.
        const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '', 10);
        if (deadMercId && activeMercenaryId && deadMercId !== activeMercenaryId) return;
        // Mark the user as dead in context immediately so MercenaryRequiredRoute
        // blocks all navigation away from the death screen.
        setUser((prev: any) => ({ ...prev, isDead: true, deathBattleReport: String(battleReportId) }));
        navigate(`/deathscreen/${battleReportId}`);
      };

      const handlePotionsUpdated = (data: { mercenaryId: number }) => {
        const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '', 10);
        if (data.mercenaryId === activeMercenaryId) {
          axios.get(`/api/inventory/${activeMercenaryId}/activePotions`, { withCredentials: true })
            .then(res => setActivePotions(
              res.data.map((p: ActivePotion) => ({
                ...p,
                remainingMs: Math.max(0, new Date(p.effectEndTime).getTime() - Date.now()),
              }))
            ))
            .catch(() => {});
        }
      };

      socket.on('update_mercenary_stats', handleStatsUpdate);
      socket.on('battle_notification', handleBattleNotification);
      socket.on('level_up_notification', handleLevelUp);
      socket.on('mercenary_regen', handleRegen);
      socket.on('friendRequestHandled', handleFriendRequestHandled);
      socket.on('newMail', handleNewMail);
      socket.on('mailRead', handleMailRead);
      socket.on('friendOnline', handleFriendOnline);
      socket.on('mercenary_died', handleMercenaryDied);
      socket.on('potions_updated', handlePotionsUpdated);

      return () => {
        socket.off('update_mercenary_stats', handleStatsUpdate);
        socket.off('battle_notification', handleBattleNotification);
        socket.off('level_up_notification', handleLevelUp);
        socket.off('mercenary_regen', handleRegen);
        socket.off('newFriendRequest');
        socket.off('friendRequestHandled', handleFriendRequestHandled);
        socket.off('newMail', handleNewMail);
        socket.off('mailRead', handleMailRead);
        socket.off('friendOnline', handleFriendOnline);
        socket.off('mercenary_died', handleMercenaryDied);
        socket.off('potions_updated', handlePotionsUpdated);
      };
    }
  }, [socket, location.pathname, navigate]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const friendResponse = await axios.get('/api/friend/pending-count', { withCredentials: true });
        setPendingFriendRequests(friendResponse.data.count);

        const mailResponse = await axios.get('/api/mail/unread-count', { withCredentials: true });
        setUnreadMails(mailResponse.data.count);
      } catch (error) {
        console.error("Error fetching notification counts:", error);
      }
    };

    fetchNotifications();
  }, []);

  // Fetch and update hospitalization info
  useEffect(() => {
    const mercId = mercenary?.id;
    if (!mercId) return;
    
    const fetchHospitalizationInfo = async () => {
      try {
        const response = await axios.get(`/api/hospitalization/mercenary/${mercId}/stats`);
        const { isCurrentlyHospitalized, hospitalizedUntil, current } = response.data;
        
        if (isCurrentlyHospitalized) {
          setHospitalizationInfo({
            battleReportId: current?.battleReportId || null,
            hospitalizedUntil: hospitalizedUntil || null,
            timeRemaining: ''
          });
          
          // Update mercenaryStats to reflect hospitalization
          setMercenaryStats(prev => prev ? {
            ...prev,
            isHospitalized: true,
            hospitalizedUntil: hospitalizedUntil
          } : prev);
        } else {
          setHospitalizationInfo(null);
        }
      } catch (error) {
        console.error('Error fetching hospitalization info:', error);
        setHospitalizationInfo(null);
      }
    };

    if (!document.hidden) {
      fetchHospitalizationInfo();
    }
    // Re-check every 30 seconds
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchHospitalizationInfo();
    }, 30000);
    return () => clearInterval(interval);
    // Only re-run when the mercenary ID actually changes, not on stat updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mercenary?.id]);

  // Update hospitalization countdown timer
  useEffect(() => {
    if (!hospitalizationInfo?.hospitalizedUntil) return;

    const updateTimer = () => {
      if (document.hidden) return;
      const now = new Date().getTime();
      const recoveryTime = new Date(hospitalizationInfo.hospitalizedUntil!).getTime();
      const difference = recoveryTime - now;

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setHospitalizationInfo(prev => prev ? {
          ...prev,
          timeRemaining: `${hours}h ${minutes}m ${seconds}s`
        } : null);
      } else {
        setHospitalizationInfo(prev => prev ? {
          ...prev,
          timeRemaining: 'Ready - refresh page'
        } : null);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [hospitalizationInfo?.hospitalizedUntil]);

  useEffect(() => {
    if (mercenary) {
      setMercenaryStats(mercenary);

      if (mercenary.allowContracts !== undefined) {
        setAllowContracts(mercenary.allowContracts);
      }

      if (mercenary.xpLocked !== undefined) {
        setXpLocked(mercenary.xpLocked);
      }

      // Show level-up notification if there are skill points, hide if there aren't
      if (mercenary.primarySkillPoints > 0 || mercenary.secondarySkillPoints > 0) {
        setLevelUpNotification(true);
      } else {
        setLevelUpNotification(false);
      }
    }
  }, [mercenary]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      setRegenTimerProgress(Date.now() % 180000);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fetch active potions and keep them synced
  useEffect(() => {
    const mercId = mercenary?.id;
    if (!mercId) return;

    const fetchActivePotions = async () => {
      try {
        const response = await axios.get(`/api/inventory/${mercId}/activePotions`, { withCredentials: true });
        setActivePotions(
          response.data.map((p: ActivePotion) => ({
            ...p,
            remainingMs: Math.max(0, new Date(p.effectEndTime).getTime() - Date.now()),
          }))
        );
      } catch {
        // silently ignore
      }
    };

    if (!document.hidden) {
      fetchActivePotions();
    }
    const syncInterval = setInterval(() => {
      if (document.hidden) return;
      fetchActivePotions();
    }, 30000);
    return () => clearInterval(syncInterval);
  }, [mercenary?.id]);

  // Tick active potion timers every second
  useEffect(() => {
    if (activePotions.length === 0) return;
    const tick = setInterval(() => {
      if (document.hidden) return;
      setActivePotions(prev =>
        prev
          .map(p => ({ ...p, remainingMs: Math.max(0, new Date(p.effectEndTime).getTime() - Date.now()) }))
          .filter(p => p.remainingMs > 0)
      );
    }, 1000);
    return () => clearInterval(tick);
  }, [activePotions.length]);

  const toggleAllowContracts = async () => {
    try {
      const updatedValue = !allowContracts;
      await axios.patch(`/api/contract/${mercenaryStats?.id}/allow-contracts`, {
        allowContracts: updatedValue,
      });
  
      setAllowContracts(updatedValue);
      setMercenaryStats((prev) => (prev ? { ...prev, allowContracts: updatedValue } : prev));
    } catch (error) {
      console.error('Failed to update allowContracts:', error);
    }
  };

  const toggleXpLocked = async () => {
    try {
      const updatedValue = !xpLocked;
      await axios.patch(`/api/mercenary/${mercenaryStats?.id}/xp-locked`, {
        xpLocked: updatedValue,
      }, { withCredentials: true });
      setXpLocked(updatedValue);
      setMercenaryStats((prev) => (prev ? { ...prev, xpLocked: updatedValue } : prev));
    } catch (error) {
      console.error('Failed to update xpLocked:', error);
    }
  };

  const setSpoilerStep = (step: 1 | 2 | 3) => {
    setSpoilerMode(step);
    localStorage.setItem('spoilerMode', String(step));
  };

  const handleCloseFriendNotification = useCallback(() => {
    setFriendLoginNotification(null);
  }, []);

  const ageStageLabel = useAgeStageLabel(mercenaryStats?.race, mercenaryStats?.age);
  const rawAge = mercenaryStats?.age;
  const ageDisplay = rawAge != null
    ? ageStageLabel ?? `${rawAge}d`
    : null;
  const ageTooltip = rawAge != null
    ? `${rawAge - 20} days in the city · Total age: ${rawAge}`
    : null;

  const updateAgeTooltipPosition = useCallback(() => {
    if (!ageBadgeRef.current) return;
    const rect = ageBadgeRef.current.getBoundingClientRect();
    setAgeTooltipPosition({
      top: Math.max(12, rect.top - 8),
      right: Math.max(12, window.innerWidth - rect.right),
    });
  }, []);

  const handleAgeTooltipEnter = useCallback(() => {
    if (!ageTooltip) return;
    updateAgeTooltipPosition();
    setShowAgeTooltip(true);
  }, [ageTooltip, updateAgeTooltipPosition]);

  const handleAgeTooltipLeave = useCallback(() => {
    setShowAgeTooltip(false);
  }, []);

  useEffect(() => {
    if (!showAgeTooltip) return;

    const handleViewportChange = () => updateAgeTooltipPosition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [showAgeTooltip, updateAgeTooltipPosition]);

  if (!mercenaryStats) {
    return <p>No mercenary found.</p>;
  }

  const currentHealth = mercenaryStats.effectiveHealth ?? mercenaryStats.health ?? 0;
  const maxHealth = mercenaryStats.effectiveMaxHealth ?? mercenaryStats.maxHealth ?? 1;
  const healthPercentage = (currentHealth / maxHealth) * 100;
  
  // Calculate HP regen preview (25% of max HP per regen tick)
  const regenAmount = Math.ceil(maxHealth * 0.25);
  const healthAfterRegen = Math.min(currentHealth + regenAmount, maxHealth);
  const healthAfterRegenPercentage = (healthAfterRegen / maxHealth) * 100;
  
  const currentLevelExp = (mercenaryStats.experience ?? 0) - (mercenaryStats.previousExperience ?? 0);
  const levelExpNeeded = (mercenaryStats.nextExperience ?? 0) - (mercenaryStats.previousExperience ?? 0);
  const hasLeveledUp = levelExpNeeded > 0 && currentLevelExp >= levelExpNeeded;
  const noExpData = levelExpNeeded <= 0;
  const experiencePercentage = hasLeveledUp ? 100 : noExpData ? 0 : (currentLevelExp / levelExpNeeded) * 100;
  const experienceDisplay = hasLeveledUp ? "Leveled up!" : noExpData ? `${currentLevelExp} Exp` : `${currentLevelExp} / ${levelExpNeeded} Exp`;

  // Calculate rested XP preview
  const restedExp = mercenaryStats.restedExp ?? 0;
  const pooledXp = mercenaryStats.pooledXp ?? 0;
  const hpBarTooltip = [
    restedExp > 0 ? `Rested XP: ${restedExp.toLocaleString()}` : null,
    pooledXp > 0 ? `Pooled XP: ${pooledXp.toLocaleString()}` : null,
  ].filter(Boolean).join('\n') || undefined;
  const expWithRested = noExpData ? 0 : Math.min(currentLevelExp + restedExp, levelExpNeeded);
  const expWithRestedPercentage = hasLeveledUp ? 100 : noExpData ? 0 : (expWithRested / levelExpNeeded) * 100;

  const minutes = Math.floor((180000 - regenTimerProgress) / 60000);
  const seconds = Math.floor(((180000 - regenTimerProgress) % 60000) / 1000);

  // 1 = always show, 2 = hide on battle-result, 3 = always hide
  const shouldHideBars =
    spoilerMode === 3 ||
    (spoilerMode === 2 && location.pathname.startsWith('/battle-result'));

  const handleDismissNotification = () => {
    setBattleNotification(null);
  };

  const handleLevelUp = () => {
    navigate('/level-up');
    setLevelUpNotification(false);
  };

  return (
    <>
    {battleNotification && ReactDOM.createPortal(
      <div className={styles.battleNotification}>
        <p>I just participated in a battle, want to see it?</p>
        <Link
          to={`/battle-result/${battleNotification.battleId}`}
          className={styles.battleNotificationViewBtn}
          onClick={() => setBattleNotification(null)}
        >
          View Battle
        </Link>
        <button onClick={handleDismissNotification}>Close</button>
      </div>,
      document.body
    )}
    {showAgeTooltip && ageTooltip && ageTooltipPosition && ReactDOM.createPortal(
      <div
        id="mercenary-age-tooltip"
        className={styles.ageTooltipPortal}
        style={{ top: ageTooltipPosition.top, right: ageTooltipPosition.right }}
      >
        {ageTooltip}
      </div>,
      document.body
    )}
    <div className={styles.mercenaryBar}>
      <div className={styles.contentContainer}>
        <div className={styles.mercenaryCard}>
          <Link
            to="/mercenary"
            className={styles.avatarLink}
            title="Open mercenary profile"
            aria-label="Open mercenary profile"
          >
          <div className={styles.avatarContainer}>
            <img
              src={`/img/avatars/${mercenaryStats.race?.toLowerCase() ?? 'default'}.${mercenaryStats.sex?.toLowerCase() ?? 'default'}.${mercenaryStats.avatar ?? 1}.png`}
              alt={mercenaryStats.name ?? 'Unknown Mercenary'}
              className={styles.avatar}
            />
            <div className={styles.levelBadge}>{mercenaryStats.level ?? 1}</div>
          </div>
          </Link>
          <div className={styles.mercenaryInfo}>
            <h2 className={styles.mercenaryName}>{mercenaryStats.name ?? 'Unknown Mercenary'}</h2>
            {mercenaryStats.title && (
              <p className={`${styles.mercenaryTitle} ${mercenaryStats.title === 'Legend' ? styles.legendTitle : ''}`}>
                {mercenaryStats.title}
              </p>
            )}
            <p className={styles.mercenaryClass}>{mercenaryStats.race} {mercenaryStats.sex}</p>
            {ageDisplay && (
              <p
                ref={ageBadgeRef}
                className={styles.mercenaryAge}
                onMouseEnter={handleAgeTooltipEnter}
                onMouseLeave={handleAgeTooltipLeave}
                onFocus={handleAgeTooltipEnter}
                onBlur={handleAgeTooltipLeave}
                tabIndex={ageTooltip ? 0 : -1}
                aria-describedby={showAgeTooltip ? 'mercenary-age-tooltip' : undefined}
              >
                {ageDisplay}
              </p>
            )}
          </div>


        </div>

        <div className={styles.mercenaryStats}>
          {!shouldHideBars && (
            <div className={styles.stat}>
              {(mercenaryStats?.deathDate || currentHealth <= 0) ? (
                <div className={`${styles.progressBar} ${styles.deadBar}`}>
                  <span>Dead</span>
                </div>
              ) : (
                <div className={`${styles.progressBar} ${styles.hpBar}`}>
                  {currentHealth < maxHealth && healthAfterRegen > currentHealth && (
                    <div 
                      className={styles.regenPreview} 
                      style={{ 
                        width: `${healthAfterRegenPercentage}%`
                      }} 
                    />
                  )}
                  <div className={styles.fill} style={{ width: `${healthPercentage}%` }} />
                  <span>{Math.floor(currentHealth)} / {Math.floor(maxHealth)} HP</span>
                </div>
              )}
            </div>
          )}
          {!shouldHideBars && (
            <div
              className={styles.hpBarWrapper}
              onMouseEnter={() => hpBarTooltip && setShowHpTooltip(true)}
              onMouseLeave={() => setShowHpTooltip(false)}
            >
              <div className={`${styles.progressBar} ${styles.epBar}`}>
                {restedExp > 0 && expWithRested > currentLevelExp && (
                  <div 
                    className={styles.restedExpPreview} 
                    style={{ 
                      width: `${expWithRestedPercentage}%`
                    }} 
                  />
                )}
                <div className={styles.fill} style={{ width: `${experiencePercentage}%` }} />
                <span>{experienceDisplay}</span>
              </div>
              {showHpTooltip && hpBarTooltip && (
                <div className={styles.hpTooltip}>
                  {hpBarTooltip.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className={styles.stat}>
            <div className={`${styles.progressBar} ${styles.regenBar}`}>
              <div className={styles.fill} style={{ width: `${(regenTimerProgress / 180000) * 100}%` }} />
              <span>{minutes}:{seconds < 10 ? `0${seconds}` : seconds} Regen</span>
            </div>
          </div>
          {/* Active potion/effect bars */}
          {activePotions.length > 0 && (
            <div className={styles.activeEffects}>
              {activePotions.map((potion, i) => {
                const fillPct = potion.totalDurationMs > 0
                  ? Math.min(100, (potion.remainingMs / potion.totalDurationMs) * 100)
                  : 0;
                const h = Math.floor(potion.remainingMs / 3600000);
                const m = Math.floor((potion.remainingMs % 3600000) / 60000);
                const s = Math.floor((potion.remainingMs % 60000) / 1000);
                const timeStr = h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
                return (
                  <div
                    key={i}
                    className={styles.effectBarWrapper}
                    onMouseEnter={() => setActiveEffectTooltip(i)}
                    onMouseLeave={() => setActiveEffectTooltip(null)}
                    onClick={() => setActiveEffectTooltip(activeEffectTooltip === i ? null : i)}
                  >
                    <div className={`${styles.progressBar} ${styles.effectBar}`}>
                      <div className={styles.fill} style={{ width: `${fillPct}%` }} />
                      <div className={styles.effectBarLabel}>
                        <span className={styles.effectBarName}>{potion.name}</span>
                      </div>
                    </div>
                    {activeEffectTooltip === i && (
                      <div className={styles.effectTooltip}>
                        <div>{potion.effect}</div>
                        <div className={styles.effectTooltipTime}>{timeStr} remaining</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className={styles.mercenaryForm}>
            <div className={styles.labelValuePair}>
              <label>Rounds:</label>
              <span>{Math.floor(mercenaryStats.rounds ?? 0)} / {mercenaryStats.maxRounds ?? 0}</span>
            </div>
            <div className={styles.labelValuePair}>
              <label>Silver:</label>
              <span>{mercenaryStats.silver}</span>
            </div>
            <div className={styles.labelValuePair}>
              <label>Reputation:</label>
              <span>{mercenaryStats.reputation}</span>
            </div>
            <div className={styles.toggleRow}>
              <label>Contracts:</label>
              <div
                className={`${styles.toggleSwitch} ${allowContracts ? styles.on : ''}`}
                onClick={toggleAllowContracts}
                title={allowContracts ? 'Contract battles enabled, click to disable' : 'Contract battles disabled, click to enable'}
              >
                <span className={styles.toggleSlider} />
              </div>
            </div>
            <div className={styles.toggleRow}>
              <label>XP Gain:</label>
              <div
                className={`${styles.toggleSwitch} ${!xpLocked ? styles.on : ''}`}
                onClick={toggleXpLocked}
                title={xpLocked ? 'XP gain is OFF, click to enable' : 'XP gain is ON, click to disable'}
              >
                <span className={styles.toggleSlider} />
              </div>
            </div>
            <div className={styles.toggleRow}>
              <label>Spoilers:</label>
              <div className={styles.spoilerSteps}>
                {([1, 2, 3] as const).map(step => (
                  <button
                    key={step}
                    className={`${styles.spoilerStep} ${spoilerMode === step ? styles.spoilerStepActive : ''}`}
                    onClick={() => setSpoilerStep(step)}
                    title={
                      step === 1 ? 'Always show HP & XP' :
                      step === 2 ? 'Hide only on battle result pages' :
                      'Always hide HP & XP'
                    }
                  >
                    {step}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {levelUpNotification && (
          <div className={styles.levelUpNotification}>
            <button onClick={handleLevelUp} className={styles.levelUpButton}>Level Up!</button>
          </div>
        )}

        {/* Hospitalization warning card */}
        {hospitalizationInfo && (
          <div className={styles.hospitalizationWarning}>
            <h4>WOUNDED</h4>
            <p className={styles.recoveryLabel}>Recovery</p>
            <p className={styles.recoveryTime}>{hospitalizationInfo.timeRemaining}</p>
            <div className={styles.hospitalizationLinks}>
              {hospitalizationInfo.battleReportId && (
                <Link 
                  to={`/battle-result/${hospitalizationInfo.battleReportId}`}
                  className={styles.battleLink}
                >
                  View Battle
                </Link>
              )}
              <Link 
                to={`/hospital/${mercenaryStats.id}`}
                className={styles.hospitalLink}
              >
                Hospital Details
              </Link>
            </div>
          </div>
        )}

        <div className={styles.accountLinks}>
          <div onClick={() => setShowAccountLinks(!showAccountLinks)} className={styles.collapsibleHeader}>
            <h3 className={styles.linksTitle}>Account</h3>
            {pendingFriendRequests > 0 || unreadMails > 0 ? (
               <span className={styles.notificationDot} title="New notifications"></span>
            ) : null}
            <span className={styles.toggleIcon}>{showAccountLinks ? '−' : '+'}</span>
          </div>

          {showAccountLinks && (
            <ul className={styles.linksList}>
              <li><Link to="/mercenaries">My Mercenaries</Link></li>
              <li>
                <Link to="/friends">Friends List</Link>
                {pendingFriendRequests > 0 && (
                  <span className={styles.notificationBadge}>{pendingFriendRequests}</span>
                )}
              </li>
              <li>
                <Link to="/mail">Mail</Link>
                {unreadMails > 0 && (
                  <span className={styles.notificationBadge}>{unreadMails}</span>
                )}
              </li>
              <li><Link to="/game-settings">Game Settings</Link></li>
              <li><Link to="/account">Account</Link></li>
            </ul>
          )}
        </div>
      </div>
      {friendLoginNotification && (
        <Toast 
          message={friendLoginNotification}
          type="info"
          duration={5000}
          onClose={handleCloseFriendNotification}
        />
      )}
    </div>
    </>
  );
};

export default MercenaryBar;
